import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { getJepxMemberInfo } from '@ebalance/proto/bffuser/v1/jepx_member_info_service-JepxMemberInfoService_connectquery'
import { GetJepxMemberInfoRequestSchema } from '@ebalance/proto/bffuser/v1/jepx_member_info_service_pb'
import { skipToken } from '@tanstack/react-query'

import {
  type JepxFeeMarket,
  feeScheduleUnitPrice,
  feeUnitPriceToYenPerKwh,
} from '~/models/jepxFee'
import {
  AUTO_BID_FEE_OFF,
  type AutoBidFeeAutoConsider,
} from '~/pages/AdminApprovalFlow/models/autoBid'

/**
 * 自動入札ルールの表示に使う手数料自動考慮の扱い (AC-APF-041 / 042 / 043 / 047)。
 * ON/OFF は入札制限 (`GetBidLimitMatrix` の `feeAutoConsiderPolicy`) から呼び出し側が渡し、
 * 単価は ON の時だけ取得する。 取得の失敗で画面全体を落とさない (`throwOnError: false`)。
 */
export function useAutoBidFee(
  market: JepxFeeMarket,
  enabled: boolean,
): AutoBidFeeAutoConsider {
  const { data } = useQuery(
    getJepxMemberInfo,
    enabled ? create(GetJepxMemberInfoRequestSchema, {}) : skipToken,
    { throwOnError: false },
  )
  const feeYenPerKwh = enabled
    ? feeUnitPriceToYenPerKwh(feeScheduleUnitPrice(data, market))
    : undefined
  return useMemo((): AutoBidFeeAutoConsider => {
    if (!enabled) return AUTO_BID_FEE_OFF
    return feeYenPerKwh === undefined
      ? { kind: 'unavailable' }
      : { kind: 'on', feeYenPerKwh }
  }, [enabled, feeYenPerKwh])
}
